import { useState } from "react";
import { toast } from "sonner";
import { EyeSlash, Eye } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const toIso = (d, end) => new Date(`${d}T${end ? "23:59:59" : "00:00:00"}`).toISOString();
const call = async (fn, msg, done) => { try { await fn(); toast.success(msg); done && done(); } catch (e) { toast.error(errMsg(e)); } };

const SeasonForm = ({ onDone }) => {
  const [f, setF] = useState({ name: "", start: "", end: "" });
  const save = () => call(() => api.post("/admin/leaderboard/seasons", { name: f.name, starts_at: toIso(f.start), ends_at: toIso(f.end, true) }), "Season created", () => { setF({ name: "", start: "", end: "" }); onDone(); });
  return (
    <div className="panel flex flex-wrap items-end gap-3 p-4 text-sm" data-testid="season-form">
      <label>Season or tour name<Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Spring 2027 Tour" data-testid="season-name-input" /></label>
      <label>Starts<Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="season-start-input" /></label>
      <label>Ends<Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="season-end-input" /></label>
      <button className="btn-navy" disabled={!f.name || !f.start || !f.end} onClick={save} data-testid="season-create-btn">Add season</button>
    </div>
  );
};

const Seasons = ({ seasons, reload, owner }) => (
  <div className="space-y-3">
    {owner && <SeasonForm onDone={reload} />}
    {seasons.length === 0 && <p className="text-sm text-[#475569]">No seasons yet. The public leaderboard shows nothing until a season is running.</p>}
    {seasons.map((s) => (
      <details key={s.id} className="panel p-4 text-sm" data-testid={`season-row-${s.id}`}>
        <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2">
          <span><strong>{s.name}</strong> · {fmtDate(s.starts_at)} to {fmtDate(s.ends_at)}</span>
          <span className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold uppercase ${s.finalized ? "bg-[#E2E8F0]" : "bg-[#1E8E5A]/15 text-[#1E8E5A]"}`}>{s.finalized ? "Archived" : new Date(s.starts_at) > new Date() ? "Upcoming" : "Running"}</span>
            {owner && !s.finalized && <button className="btn-outline px-2 py-1 text-xs" onClick={(e) => { e.preventDefault(); if (window.confirm("Close this season now? Standings are archived and each city's #1 gets the Top Crew Builder badge.")) call(() => api.post(`/admin/leaderboard/seasons/${s.id}/close`), "Season closed and archived", reload); }} data-testid={`season-close-${s.id}`}>Close now</button>}
          </span>
        </summary>
        {s.finalized && (
          <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid={`season-archive-${s.id}`}>
            {(s.archive || []).length === 0 && <p className="text-[#475569]">No entries in this season.</p>}
            {(s.archive || []).map((b) => <div key={b.city_id}><div className="eyebrow">{b.city_name}</div>{b.entries.map((e) => <div key={e.rank}>#{e.rank} {e.display} · {e.count}</div>)}</div>)}
          </div>
        )}
      </details>
    ))}
  </div>
);

const Recognise = ({ entry, board, seasonId, onDone }) => {
  const [type, setType] = useState("");
  const go = (t) => call(() => api.post("/admin/leaderboard/recognitions", { user_id: entry.user_id, type: t, city_id: board.city_id, season_id: seasonId }), "Recognition recorded", () => { setType(""); onDone(); });
  return (
    <select value={type} onChange={(e) => e.target.value && go(e.target.value)} className="h-8 rounded-md border px-2 text-xs" data-testid={`recognise-${entry.user_id}`}>
      <option value="">Recognise…</option><option value="badge">Top Crew Builder badge</option><option value="shoutout">On-stage shout-out</option><option value="vip_upgrade">VIP seat upgrade</option>
    </select>
  );
};

const Standings = ({ seasons, onChange }) => {
  const [sid, setSid] = useState("");
  const { data, reload } = useFetch(`/admin/leaderboard/standings${sid ? `?season_id=${sid}` : ""}`);
  const hide = (e) => {
    const reason = window.prompt(e.hidden ? "Reason for showing this entry again" : "Reason for hiding this entry") ;
    if (reason === null) return;
    call(() => api.post(`/admin/leaderboard/entries/${e.user_id}/hide`, { hidden: !e.hidden, reason }), e.hidden ? "Entry shown" : "Entry hidden", reload);
  };
  return (
    <div className="space-y-3">
      <select value={sid} onChange={(e) => setSid(e.target.value)} className="h-10 rounded-md border bg-white px-3 text-sm" data-testid="standings-season-select">
        <option value="">Current season</option>{seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      {!data ? null : !data.season ? <p className="text-sm text-[#475569]">No season is running.</p> : data.boards.length === 0 ? <p className="text-sm text-[#475569]" data-testid="standings-empty">No opted-in students with attended referrals yet.</p> : (
        <div className="grid gap-3 md:grid-cols-2">
          {data.boards.map((b) => (
            <div key={b.city_id} className="panel p-4 text-sm" data-testid={`standings-${b.city_id}`}>
              <div className="eyebrow mb-2">{b.city_name}</div>
              {b.entries.map((e) => (
                <div key={e.user_id} className={`flex flex-wrap items-center justify-between gap-2 border-t py-2 ${e.hidden ? "opacity-50" : ""}`} data-testid={`standings-entry-${e.user_id}`}>
                  <span>{e.hidden ? "Hidden" : `#${e.rank}`} · {e.display} · {e.count} attended</span>
                  <span className="flex gap-2"><Recognise entry={e} board={b} seasonId={data.season.id} onDone={onChange} /><button className="btn-outline px-2 py-1 text-xs" onClick={() => hide(e)} data-testid={`hide-toggle-${e.user_id}`}>{e.hidden ? <><Eye /> Show</> : <><EyeSlash /> Hide</>}</button></span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Contest = ({ owner }) => {
  const { data, reload } = useFetch("/admin/leaderboard/contest");
  const [rules, setRules] = useState(null);
  if (!data) return null;
  const put = (body, msg) => call(() => api.put("/admin/leaderboard/contest", body), msg, () => { setRules(null); reload(); });
  return (
    <div className="panel space-y-3 p-5 text-sm" data-testid="contest-card">
      <p>Leaders get recognition only by default. A cash or prize contest stays off until the owner writes and approves the contest rules, then switches it on.</p>
      <Textarea rows={6} disabled={!owner} value={rules ?? data.rules} onChange={(e) => setRules(e.target.value)} placeholder="Contest rules (prizes, eligibility, dates, how winners are picked)" data-testid="contest-rules-input" />
      <div className="text-xs text-[#475569]" data-testid="contest-approval-status">{data.rules_approved_at ? `Rules approved by ${data.rules_approved_by} on ${fmtDateTime(data.rules_approved_at)}` : "Rules not approved"}</div>
      {owner && (
        <div className="flex flex-wrap items-center gap-3">
          <button className="btn-outline" disabled={rules == null} onClick={() => put({ rules }, "Rules saved. Approval reset.")} data-testid="contest-save-rules-btn">Save rules</button>
          <button className="btn-outline" disabled={rules != null || !data.rules || !!data.rules_approved_at} onClick={() => put({ approve_rules: true }, "Rules approved")} data-testid="contest-approve-btn">Approve rules</button>
          <label className="flex items-center gap-2 font-semibold"><Switch checked={data.enabled} disabled={!data.rules_approved_at} onCheckedChange={(v) => put({ enabled: v }, v ? "Contest switched on" : "Contest switched off")} data-testid="contest-enabled-switch" /> Contest {data.enabled ? "on" : "off"}</label>
        </div>
      )}
    </div>
  );
};

export default function LeaderboardAdmin() {
  const { user } = useAuth();
  const owner = user.role === "owner";
  const { data: seasons, reload: reloadSeasons } = useFetch("/admin/leaderboard/seasons");
  const { data: recs, reload: reloadRecs } = useFetch("/admin/leaderboard/recognitions");
  const [tick, setTick] = useState(0);
  const reload = () => { reloadSeasons(); reloadRecs(); setTick((t) => t + 1); };
  return (
    <div className="space-y-8" data-testid="leaderboard-admin">
      <section><h3 className="mb-2 font-display text-xl font-bold uppercase">Live standings</h3><Standings key={tick} seasons={seasons || []} onChange={reloadRecs} /></section>
      <section><h3 className="mb-2 font-display text-xl font-bold uppercase">Seasons and archive</h3><Seasons seasons={seasons || []} reload={reload} owner={owner} /></section>
      <section>
        <h3 className="mb-2 font-display text-xl font-bold uppercase">Recognitions given</h3>
        {(recs || []).length === 0 ? <p className="text-sm text-[#475569]">None yet.</p> : <div className="panel divide-y text-sm">{recs.map((r) => <div key={r.id} className="flex flex-wrap justify-between gap-2 p-3" data-testid={`recognition-${r.id}`}><span><strong>{r.display}</strong> · {r.label}{r.season_name ? ` · ${r.season_name}` : ""}{r.note ? ` · ${r.note}` : ""}</span><span className="text-xs text-[#475569]">{r.by} · {fmtDate(r.created_at)}</span></div>)}</div>}
      </section>
      <section><h3 className="mb-2 font-display text-xl font-bold uppercase">Prize contest</h3><Contest owner={owner} /></section>
    </div>
  );
}
